import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash2, RotateCcw, ShieldAlert, Car } from "lucide-react";
import { PageHeader } from "../components/Layout";
import { Button, Field } from "../components/UIKit";
import { api } from "../api/client";

// -----------------------------------------------------------------------
// Route: /profile/deleted-vehicles (protected)
//
// Renders the per-user trash of vehicles that were soft-deleted from the
// active fleet. Each entry can be:
//   * Restored (POST /vehicles/{vid}/restore) — idempotent
//   * Permanently deleted (DELETE /vehicles/{vid}/permanent) via a
//     two-step confirmation (warning → type "SUPPRIMER" exactly).
//
// The page never renders vehicles that are still active — the API
// filters that server-side. On a permanent-delete error the card stays
// visible so the user can retry (the backend cascade is idempotent).
// -----------------------------------------------------------------------
function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("fr-FR", {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch {
    return "";
  }
}

function vehicleLabel(v) {
  const parts = [v?.brand, v?.model].filter(Boolean).join(" ").trim();
  const plate = v?.plate ? ` — ${v.plate}` : "";
  return parts ? `${parts}${plate}` : (v?.plate || "Véhicule");
}

export default function DeletedVehicles() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // permanent-delete flow modal state:
  //   null | { step: 1|2, vehicle, confirmation, error }
  const [permModal, setPermModal] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get("/vehicles/deleted");
      setItems(data?.vehicles || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Impossible de charger la corbeille.");
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onRestore = async (v) => {
    if (busyId) return;
    setBusyId(v.id);
    try {
      await api.post(`/vehicles/${v.id}/restore`);
      toast.success("Véhicule restauré", {
        description: "Il est de nouveau disponible dans votre flotte.",
      });
      setItems((prev) => (prev || []).filter((it) => it.id !== v.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restauration impossible");
    } finally {
      setBusyId(null);
    }
  };

  const openPermanent = (v) => {
    setPermModal({ step: 1, vehicle: v, confirmation: "", error: null });
  };
  const closePerm = () => setPermModal(null);

  const proceedToStep2 = () => {
    setPermModal((m) => (m ? { ...m, step: 2, confirmation: "", error: null } : m));
  };

  const onPermanentConfirm = async () => {
    if (!permModal) return;
    if (permModal.confirmation !== "SUPPRIMER") return;
    const v = permModal.vehicle;
    setBusyId(v.id);
    setPermModal((m) => (m ? { ...m, error: null } : m));
    try {
      await api.delete(`/vehicles/${v.id}/permanent`);
      toast.success("Véhicule supprimé définitivement", {
        description: "Le véhicule et ses données associées ont été supprimés.",
      });
      setItems((prev) => (prev || []).filter((it) => it.id !== v.id));
      setPermModal(null);
    } catch (e) {
      // Keep the card visible + surface the error inside the modal so
      // the user can retry (the backend cascade is idempotent).
      setPermModal((m) => (m ? {
        ...m,
        error: e?.response?.data?.detail || "Suppression impossible. Réessayez.",
      } : m));
    } finally {
      setBusyId(null);
    }
  };

  const count = items?.length ?? 0;

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", paddingBottom: 32 }} data-testid="deleted-vehicles-page">
      <div style={{ padding: "16px 20px 0" }}>
        <button
          onClick={() => navigate(-1)}
          data-testid="deleted-vehicles-back"
          aria-label="Retour"
          style={{
            width: 40, height: 40, borderRadius: 20,
            background: "var(--mf-bg-muted)", border: 0, cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            color: "var(--mf-text)",
          }}
        >
          <ArrowLeft size={20} />
        </button>
      </div>
      <PageHeader
        title="Véhicules supprimés"
        subtitle="Retrouvez, restaurez ou supprimez définitivement les véhicules placés dans la corbeille."
      />

      <div style={{ padding: "0 20px" }}>
        {items === null && (
          <SkeletonList />
        )}

        {items && items.length === 0 && !error && (
          <EmptyState />
        )}

        {items && items.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="deleted-vehicles-list">
            {items.map((v) => (
              <VehicleTrashCard
                key={v.id}
                vehicle={v}
                busy={busyId === v.id}
                onRestore={() => onRestore(v)}
                onPermanent={() => openPermanent(v)}
              />
            ))}
          </div>
        )}

        {error && (
          <div
            role="alert"
            data-testid="deleted-vehicles-error"
            style={{
              marginTop: 20, padding: "12px 14px", borderRadius: 12,
              background: "rgba(239,68,68,0.08)", color: "var(--mf-urgent-solid)",
              fontSize: 14,
            }}
          >
            {error}
          </div>
        )}
      </div>

      {permModal && permModal.step === 1 && (
        <PermanentStep1
          vehicle={permModal.vehicle}
          onCancel={closePerm}
          onContinue={proceedToStep2}
        />
      )}
      {permModal && permModal.step === 2 && (
        <PermanentStep2
          vehicle={permModal.vehicle}
          confirmation={permModal.confirmation}
          onChange={(val) => setPermModal((m) => (m ? { ...m, confirmation: val } : m))}
          onCancel={closePerm}
          onConfirm={onPermanentConfirm}
          error={permModal.error}
          busy={busyId === permModal.vehicle.id}
        />
      )}
    </div>
  );
}

function SkeletonList() {
  return (
    <div aria-label="Chargement" data-testid="deleted-vehicles-skeleton" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{
          padding: 16, borderRadius: 16,
          background: "var(--mf-bg-card)", border: "1px solid var(--mf-divider)",
          display: "flex", gap: 12, alignItems: "center", minHeight: 92,
        }}>
          <div style={{ width: 64, height: 64, borderRadius: 12, background: "var(--mf-bg-muted)" }} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ width: "60%", height: 14, borderRadius: 4, background: "var(--mf-bg-muted)" }} />
            <div style={{ width: "40%", height: 12, borderRadius: 4, background: "var(--mf-bg-muted)" }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div
      data-testid="deleted-vehicles-empty"
      style={{
        marginTop: 40, padding: 24, textAlign: "center",
        background: "var(--mf-bg-card)", borderRadius: 20,
        border: "1px solid var(--mf-divider)",
      }}
    >
      <div style={{
        width: 56, height: 56, borderRadius: 28,
        background: "var(--mf-bg-muted)", display: "inline-flex",
        alignItems: "center", justifyContent: "center", marginBottom: 12,
        color: "var(--mf-text-secondary)",
      }}>
        <Trash2 size={26} />
      </div>
      <h2 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 700 }}>Aucun véhicule supprimé</h2>
      <p style={{ margin: 0, color: "var(--mf-text-secondary)", fontSize: 14, lineHeight: 1.5 }}>
        Les véhicules placés dans la corbeille apparaîtront ici.
      </p>
    </div>
  );
}

function VehicleTrashCard({ vehicle, busy, onRestore, onPermanent }) {
  const label = vehicleLabel(vehicle);
  const kmText = vehicle?.mileage != null
    ? `${new Intl.NumberFormat("fr-FR").format(vehicle.mileage)} km`
    : null;
  const deletedText = vehicle?.deleted_at ? `Placé dans la corbeille le ${formatDate(vehicle.deleted_at)}` : null;

  return (
    <div
      data-testid={`deleted-vehicle-card-${vehicle.id}`}
      style={{
        padding: 14, borderRadius: 16,
        background: "var(--mf-bg-card)", border: "1px solid var(--mf-divider)",
        boxShadow: "var(--mf-shadow)",
        display: "flex", flexDirection: "column", gap: 14,
      }}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{
          width: 64, height: 64, borderRadius: 12, overflow: "hidden",
          background: "var(--mf-bg-muted)", display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          {vehicle?.photo ? (
            <img src={vehicle.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <Car size={26} color="var(--mf-text-secondary)" />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--mf-text)", lineHeight: 1.3 }}>
            {label}
          </div>
          {(vehicle?.year || kmText) && (
            <div style={{ fontSize: 13, color: "var(--mf-text-secondary)", marginTop: 4 }}>
              {[vehicle?.year, kmText].filter(Boolean).join(" · ")}
            </div>
          )}
          {deletedText && (
            <div style={{ fontSize: 12, color: "var(--mf-text-muted)", marginTop: 6 }}>
              {deletedText}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <Button
          variant="primary"
          fullWidth
          disabled={busy}
          onClick={onRestore}
          testID={`deleted-vehicle-restore-${vehicle.id}`}
        >
          <RotateCcw size={16} style={{ marginRight: 6, verticalAlign: -3 }} />
          Restaurer
        </Button>
        <Button
          variant="danger"
          fullWidth
          disabled={busy}
          onClick={onPermanent}
          testID={`deleted-vehicle-permanent-${vehicle.id}`}
        >
          <Trash2 size={16} style={{ marginRight: 6, verticalAlign: -3 }} />
          Supprimer définitivement
        </Button>
      </div>
    </div>
  );
}

function ModalShell({ children, onCancel, testID }) {
  return (
    <div
      onClick={onCancel}
      data-testid={testID}
      style={{
        position: "fixed", inset: 0, background: "rgba(11,25,44,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, zIndex: 200,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--mf-bg-card)", borderRadius: 20, padding: 24,
          width: "100%", maxWidth: 420, boxShadow: "var(--mf-shadow-strong)",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function PermanentStep1({ vehicle, onCancel, onContinue }) {
  const label = vehicleLabel(vehicle);
  return (
    <ModalShell onCancel={onCancel} testID="deleted-vehicle-perm-modal-step1">
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
        <span style={{
          width: 40, height: 40, borderRadius: 20,
          background: "rgba(239,68,68,0.10)",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          color: "var(--mf-urgent-solid)", flexShrink: 0,
        }}>
          <ShieldAlert size={20} />
        </span>
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: "var(--mf-text)" }}>
          Supprimer définitivement ce véhicule&nbsp;?
        </h3>
      </div>
      <p style={{
        margin: "0 0 14px", fontSize: 14, color: "var(--mf-text-secondary)", lineHeight: 1.55,
      }}>
        Cette action supprimera définitivement le véhicule&nbsp;
        <strong style={{ color: "var(--mf-text)" }}>{label}</strong>
        &nbsp;ainsi que ses documents, entretiens, interventions, factures,
        photos et historique. <strong>Cette action est irréversible.</strong>
      </p>
      <div style={{ display: "flex", gap: 10 }}>
        <Button variant="secondary" fullWidth onClick={onCancel} testID="deleted-vehicle-perm-step1-cancel">
          Annuler
        </Button>
        <Button variant="danger" fullWidth onClick={onContinue} testID="deleted-vehicle-perm-step1-continue">
          Continuer
        </Button>
      </div>
    </ModalShell>
  );
}

function PermanentStep2({ vehicle, confirmation, onChange, onCancel, onConfirm, error, busy }) {
  const label = vehicleLabel(vehicle);
  const isMatch = confirmation === "SUPPRIMER";
  return (
    <ModalShell onCancel={onCancel} testID="deleted-vehicle-perm-modal-step2">
      <h3 style={{ margin: "0 0 6px", fontSize: 17, fontWeight: 800, color: "var(--mf-text)" }}>
        Confirmer la suppression
      </h3>
      <p style={{ margin: "0 0 12px", fontSize: 14, color: "var(--mf-text-secondary)", lineHeight: 1.55 }}>
        Véhicule à supprimer&nbsp;:{" "}
        <strong style={{ color: "var(--mf-text)" }}>{label}</strong>
      </p>
      <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--mf-text-secondary)", lineHeight: 1.55 }}>
        Pour confirmer la suppression définitive de ce véhicule, saisissez{" "}
        <code style={{
          background: "rgba(11,25,44,0.06)", padding: "1px 6px", borderRadius: 4,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontWeight: 700,
        }}>SUPPRIMER</code>{" "}
        ci-dessous.
      </p>
      <Field
        testID="deleted-vehicle-perm-input"
        label=""
        value={confirmation}
        onChange={onChange}
        placeholder="SUPPRIMER"
        autoCapitalize="characters"
      />
      {error && (
        <p
          role="alert"
          data-testid="deleted-vehicle-perm-error"
          style={{
            margin: "8px 0 0", fontSize: 13, color: "var(--mf-urgent-solid)",
            background: "rgba(239,68,68,0.08)", padding: "10px 12px", borderRadius: 10,
          }}
        >
          {error}
        </p>
      )}
      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
        <Button variant="secondary" fullWidth onClick={onCancel} testID="deleted-vehicle-perm-step2-cancel" disabled={busy}>
          Annuler
        </Button>
        <Button
          variant="danger"
          fullWidth
          onClick={onConfirm}
          disabled={!isMatch || busy}
          loading={busy}
          testID="deleted-vehicle-perm-step2-confirm"
        >
          Supprimer définitivement
        </Button>
      </div>
    </ModalShell>
  );
}
